import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import api from '../../lib/api';
import { toast } from 'sonner';
import { HexColorPicker } from 'react-colorful';
import { 
  Palette, 
  Upload, 
  Save, 
  Loader2, 
  Eye,
  Trash2,
  Building2,
  RefreshCw
} from 'lucide-react';

export default function BrandingSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [config, setConfig] = useState({
    primaryColor: '#2054A6',
    accentColor: '#F97316',
    logoUrl: null,
    companyDisplayName: '',
    companyName: ''
  });
  
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showAccentPicker, setShowAccentPicker] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await api.get('/branding');
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching branding:', error);
      // Use defaults if not configured
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/branding', {
        primaryColor: config.primaryColor,
        accentColor: config.accentColor,
        companyDisplayName: config.companyDisplayName
      });
      toast.success('Personnalisation enregistrée');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le logo ne doit pas dépasser 5 Mo');
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    setUploadingLogo(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        // Resize image if needed
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const maxSize = 400;
          let { width, height } = img;
          
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height / width) * maxSize;
              width = maxSize;
            } else {
              width = (width / height) * maxSize;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const resizedBase64 = canvas.toDataURL('image/png', 0.9);
          
          // Upload
          try {
            await api.post('/branding/logo', { logoUrl: resizedBase64 });
            setConfig(prev => ({ ...prev, logoUrl: resizedBase64 }));
            toast.success('Logo mis à jour');
          } catch (error) {
            toast.error('Erreur lors de l\'upload du logo');
          } finally {
            setUploadingLogo(false);
          }
        };
        img.src = base64;
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur lors du traitement de l\'image');
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.put('/branding', { logoUrl: '' });
      setConfig(prev => ({ ...prev, logoUrl: null }));
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Aperçu
          </CardTitle>
          <CardDescription>
            Voici comment vos couleurs et votre logo apparaîtront dans l'interface
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className="rounded-lg overflow-hidden border"
            style={{ borderColor: config.primaryColor }}
          >
            {/* Header preview */}
            <div 
              className="h-16 flex items-center justify-between px-4"
              style={{ backgroundColor: config.primaryColor }}
            >
              {config.logoUrl ? (
                <img 
                  src={config.logoUrl} 
                  alt="Logo" 
                  className="h-10 object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              ) : (
                <div className="flex items-center gap-2 text-white">
                  <Building2 className="w-6 h-6" />
                  <span className="font-semibold">
                    {config.companyDisplayName || config.companyName || 'Votre entreprise'}
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <div 
                  className="w-8 h-8 rounded-full bg-white/20"
                />
              </div>
            </div>
            
            {/* Content preview */}
            <div className="p-4 bg-slate-50">
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12" style={{ backgroundColor: config.primaryColor }}>
                  <AvatarFallback className="text-white">JD</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">John Doe</p>
                  <p className="text-sm" style={{ color: config.primaryColor }}>
                    Directeur Général
                  </p>
                </div>
                <Button
                  size="sm"
                  className="ml-auto"
                  style={{ backgroundColor: config.accentColor }}
                >
                  Action
                </Button>
              </div>
            </div>
            
            {/* Footer preview */}
            <div 
              className="h-2"
              style={{ backgroundColor: config.accentColor }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Logo Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Logo de l'entreprise
          </CardTitle>
          <CardDescription>
            Le logo sera affiché sur le trombinoscope, l'organigramme et les exports PDF
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            {/* Current Logo */}
            <div 
              className="w-32 h-32 rounded-lg border-2 border-dashed flex items-center justify-center bg-slate-50"
              style={{ borderColor: config.primaryColor }}
            >
              {config.logoUrl ? (
                <img 
                  src={config.logoUrl} 
                  alt="Logo" 
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <Building2 className="w-12 h-12 text-slate-300" />
              )}
            </div>

            {/* Upload Controls */}
            <div className="space-y-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                variant="outline"
                className="w-full"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploadingLogo ? 'Upload...' : 'Changer le logo'}
              </Button>
              {config.logoUrl && (
                <Button
                  onClick={handleRemoveLogo}
                  variant="ghost"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              )}
              <p className="text-xs text-slate-500">
                PNG, JPG ou SVG. Max 5 Mo. Recommandé: 400x100px
              </p>
            </div>
          </div>

          {/* Company Display Name */}
          <div className="pt-4 border-t">
            <Label htmlFor="displayName">Nom affiché</Label>
            <Input
              id="displayName"
              value={config.companyDisplayName || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, companyDisplayName: e.target.value }))}
              placeholder={config.companyName || 'Nom de l\'entreprise'}
              className="mt-1"
            />
            <p className="text-xs text-slate-500 mt-1">
              Ce nom sera affiché sur les documents et les pages publiques
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Colors Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Couleurs de l'interface
          </CardTitle>
          <CardDescription>
            Personnalisez les couleurs pour correspondre à votre charte graphique
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Primary Color */}
          <div className="space-y-2">
            <Label>Couleur principale</Label>
            <p className="text-xs text-slate-500 mb-2">
              Utilisée pour les en-têtes, les boutons principaux et les accents
            </p>
            <div className="flex items-center gap-4">
              <button
                className="w-12 h-12 rounded-lg border-2 border-slate-200 shadow-sm cursor-pointer transition-transform hover:scale-105"
                style={{ backgroundColor: config.primaryColor }}
                onClick={() => {
                  setShowPrimaryPicker(!showPrimaryPicker);
                  setShowAccentPicker(false);
                }}
              />
              <Input
                value={config.primaryColor}
                onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="w-32 font-mono"
                placeholder="#2054A6"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfig(prev => ({ ...prev, primaryColor: '#2054A6' }))}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Réinitialiser
              </Button>
            </div>
            {showPrimaryPicker && (
              <div className="mt-2 p-3 bg-white rounded-lg border shadow-lg inline-block">
                <HexColorPicker
                  color={config.primaryColor}
                  onChange={(color) => setConfig(prev => ({ ...prev, primaryColor: color }))}
                />
              </div>
            )}
          </div>

          {/* Accent Color */}
          <div className="space-y-2">
            <Label>Couleur d'accent</Label>
            <p className="text-xs text-slate-500 mb-2">
              Utilisée pour les badges, les highlights et les éléments secondaires
            </p>
            <div className="flex items-center gap-4">
              <button
                className="w-12 h-12 rounded-lg border-2 border-slate-200 shadow-sm cursor-pointer transition-transform hover:scale-105"
                style={{ backgroundColor: config.accentColor }}
                onClick={() => {
                  setShowAccentPicker(!showAccentPicker);
                  setShowPrimaryPicker(false);
                }}
              />
              <Input
                value={config.accentColor}
                onChange={(e) => setConfig(prev => ({ ...prev, accentColor: e.target.value }))}
                className="w-32 font-mono"
                placeholder="#F97316"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfig(prev => ({ ...prev, accentColor: '#F97316' }))}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Réinitialiser
              </Button>
            </div>
            {showAccentPicker && (
              <div className="mt-2 p-3 bg-white rounded-lg border shadow-lg inline-block">
                <HexColorPicker
                  color={config.accentColor}
                  onChange={(color) => setConfig(prev => ({ ...prev, accentColor: color }))}
                />
              </div>
            )}
          </div>

          {/* Preset Colors */}
          <div className="pt-4 border-t">
            <Label>Palettes prédéfinies</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {[
                { primary: '#2054A6', accent: '#F97316', name: 'Défaut' },
                { primary: '#059669', accent: '#F59E0B', name: 'Émeraude' },
                { primary: '#7C3AED', accent: '#EC4899', name: 'Violet' },
                { primary: '#DC2626', accent: '#0EA5E9', name: 'Rouge' },
                { primary: '#0F766E', accent: '#FB923C', name: 'Teal' },
                { primary: '#1D4ED8', accent: '#10B981', name: 'Bleu' },
              ].map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => setConfig(prev => ({
                    ...prev,
                    primaryColor: preset.primary,
                    accentColor: preset.accent
                  }))}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:border-slate-400 transition-colors"
                >
                  <div className="flex">
                    <div 
                      className="w-5 h-5 rounded-l"
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="w-5 h-5 rounded-r"
                      style={{ backgroundColor: preset.accent }}
                    />
                  </div>
                  <span className="text-sm">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="px-6"
          style={{ backgroundColor: config.primaryColor }}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>
    </div>
  );
}
